import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExam } from '../../context/ExamContext';
import api from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useHelp } from '../../context/HelpContext';
import ExamResults from './components/ExamResults';
import ExamHeader from './components/ExamHeader';
import ExamMiniNav from './components/ExamMiniNav';
import ExamGridOverlay from './components/ExamGridOverlay';
import ExamQuestion from './components/ExamQuestion';
import useExamPrefs from '../../hooks/dashboard/useExamPrefs';



const ExamSession = () => {
    const navigate = useNavigate();
    const { language, setLanguage } = useLanguage();
    const { theme, setTheme } = useTheme();
    const {
        onDemandAnswers,
        questions,
        answers,
        currentQuestionIndex,
        setCurrentQuestionIndex: setQuestionIndexRaw,
        mode,
        isPaused,
        setIsPaused,
        isFinished,
        timeInSeconds,
        isLoading,
        submitAnswer,
        markQuestionSeen,
        loadAnswers,
        finishExam,
        restartExam,
        stopExam,
        moduleId: levelId,
        category,
        minScore: ctxMinScore,
        minSubScore: ctxMinSubScore,
        matiereResults,
        examId,
        hasAnnexe,
        annexeExamId,
        historicalScore,
        historicalDate,
        isVuExamen,
        examTitle,
    } = useExam();

    // Wrapper to mark current question as seen when navigating away
    const setCurrentQuestionIndex = useCallback((newIndex) => {
        const currentQuestion = questions[currentQuestionIndex];
        // Mark as seen if leaving without answering (classic mode only — flash mode requires answering before navigating)
        if (currentQuestion && !isFinished && mode !== 'flash' && answers[currentQuestion.id] === undefined) {
            markQuestionSeen(currentQuestion.id);
        }
        setQuestionIndexRaw(newIndex);
    }, [questions, currentQuestionIndex, isFinished, mode, answers, markQuestionSeen, setQuestionIndexRaw]);

    const { startTour, hasSeenPage, helpPrefs, isPrefsSynced, registerOverlay, unregisterOverlay } = useHelp();
    const { pauseSec, showAllAnswers, setPauseSec, setShowAllAnswers } = useExamPrefs();

    // Register this overlay so background page tours are blocked
    useEffect(() => {
        registerOverlay('exam-session');
        return () => unregisterOverlay('exam-session');
    }, [registerOverlay, unregisterOverlay]);

    // Cleanup auto-advance timer on unmount
    useEffect(() => {
        return () => { if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current); };
    }, []);

    // Auto-pause après N s d'inactivité (configurable via examPrefs ; pauseSec <= 0 = jamais).
    const inactivityTimerRef = useRef(null);
    useEffect(() => {
        if (isFinished || isPaused || isLoading || pauseSec <= 0) {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            return;
        }
        const INACTIVITY_DELAY = pauseSec * 1000;
        const resetTimer = () => {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = setTimeout(() => setIsPaused(true), INACTIVITY_DELAY);
        };
        const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
        resetTimer(); // start the first countdown
        return () => {
            events.forEach(e => window.removeEventListener(e, resetTimer));
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        };
    }, [isFinished, isPaused, isLoading, setIsPaused, pauseSec]);

    const [maxDurationMinutes, setMaxDurationMinutes] = useState(null);

    // Fetch max allowed time from contentTableTemps for this level/type
    useEffect(() => {
        if (!levelId) return;
        api.get('/exams.php', { params: { action: 'get_timer', level_id: levelId, category: category || 'complet' } })
            .then(res => setMaxDurationMinutes(res.data?.timerMinutes ?? null))
            .catch(() => {});
    }, [levelId, category]);

    const [showGrid, setShowGrid] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showRestartModal, setShowRestartModal] = useState(false);
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isReviewMode, setIsReviewMode] = useState(false);
    const autoAdvanceTimerRef = useRef(null);

    // True when reviewing a vu-examen from history (new questions may exist)
    const isHistoryVuReview = useMemo(() => {
        return isVuExamen && String(examId ?? '').startsWith('review-');
    }, [isVuExamen, examId]);

    // Passing score from ExamContext (sourced from DB via stats API)
    const passingScore = useMemo(() => {
        if (category && category !== 'full' && ctxMinSubScore > 0) {
            return ctxMinSubScore;
        }
        return ctxMinScore || 75;
    }, [category, ctxMinScore, ctxMinSubScore]);



    // Score Calculation — use historical score for review sessions (legacy answers may not match current IDs)
    const calculatedScore = useMemo(() => {
        if (historicalScore !== null && historicalScore !== undefined) return historicalScore;
        if (!questions.length || Object.keys(answers).length === 0) return 0;
        const correctCount = questions.reduce((acc, q) => {
            const secret = onDemandAnswers[q.id];
            const userAnsId = answers[q.id];
            return acc + (userAnsId !== undefined && secret && String(userAnsId) === String(secret.correctReponseId) ? 1 : 0);
        }, 0);
        return Math.round((correctCount / questions.length) * 100);
    }, [questions, answers, onDemandAnswers, historicalScore]);

    // Count actual answers (exclude 0 which means "seen but not answered")
    // Must be before early returns to respect React hooks rules
    const answeredCount = useMemo(() => {
        return Object.values(answers).filter(a => a !== 0 && a !== '0' && a !== undefined && a !== null).length;
    }, [answers]);

    const currentQuestion = questions[currentQuestionIndex];

    // Derived showFeedback: true when we have both user answer AND correct answer data
    // This eliminates timing issues with setState-based showFeedback
    const showFeedback = useMemo(() => {
        if (!currentQuestion) return false;
        if (isReviewMode) return true;
        if (mode !== 'flash') return false;
        const userAnswer = answers[currentQuestion.id];
        return userAnswer !== undefined && userAnswer !== 0 && userAnswer !== '0' &&
            onDemandAnswers[currentQuestion.id] != null;
    }, [currentQuestion, mode, answers, onDemandAnswers, isReviewMode]);

    // Auto-activate review mode ONLY for history review sessions (startReviewFromHistory)
    // Normal finishExam() should show ExamResults first, not skip to review
    const hasActivatedReview = useRef(false);

    useEffect(() => {
        const examIdStr = String(examId ?? '');
        const isHistoryReview = examIdStr.startsWith('review-') || examIdStr.startsWith('custom-review-');
        if (isFinished && questions.length > 0 && !hasActivatedReview.current && isHistoryReview) {
            setIsReviewMode(true);
            hasActivatedReview.current = true;
        }
        // Reset when starting a new session (isFinished becomes false)
        if (!isFinished) {
            setIsReviewMode(false);
            hasActivatedReview.current = false;
        }
    }, [isFinished, questions.length, examId]);

    // Auto-trigger help tour on first exam session
    useEffect(() => {
        if (!isPrefsSynced) return;
        if (helpPrefs.globalEnabled && helpPrefs.autoTrigger && !hasSeenPage('exam-session') && questions.length > 0 && !isLoading && !isFinished) {
            const timer = setTimeout(() => startTour('exam-session'), 1000);
            return () => clearTimeout(timer);
        }
    }, [isPrefsSynced, questions.length, isLoading, isFinished, helpPrefs.globalEnabled, helpPrefs.autoTrigger, hasSeenPage]);

    // Auto-show validate modal when all questions are answered (classic mode only)
    useEffect(() => {
        if (questions.length === 0 || isFinished || isReviewMode || mode === 'flash') return;
        const realAnswers = Object.values(answers).filter(a => a !== 0 && a !== '0' && a !== undefined && a !== null).length;
        if (realAnswers === questions.length) {
            setShowValidateModal(true);
        }
    }, [answers, questions.length, isFinished, isReviewMode, mode]);

    // Reset selectedOption when question changes
    useEffect(() => {
        if (!currentQuestion) return;
        setSelectedOption(answers[currentQuestion.id] !== undefined ? answers[currentQuestion.id] : null);
    }, [currentQuestionIndex, answers, currentQuestion]);

    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-gray-50 dark:bg-black flex items-center justify-center text-gray-900 dark:text-white transition-colors duration-500">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 dark:border-white"></div>
            </div>
        );
    }

    if (!questions.length) {
        return (
            <div className="fixed inset-0 bg-gray-50 dark:bg-black flex flex-col items-center justify-center text-gray-900 dark:text-white p-6 text-center transition-colors duration-500">
                <h2 className="text-2xl font-bold mb-4">{language === 'fr' ? 'Session expirée' : 'Session expired'}</h2>
                <button
                    onClick={() => stopExam()}
                    className="px-6 py-2 bg-gray-200 dark:bg-white/20 rounded-xl hover:bg-gray-300 dark:hover:bg-white/30 transition font-medium"
                >
                    {language === 'fr' ? 'Fermer' : 'Close'}
                </button>
            </div>
        );
    }

    // Safety guard: if currentQuestionIndex is out of bounds, clamp it
    if (!currentQuestion && questions.length > 0) {
        const clampedIndex = Math.min(currentQuestionIndex, questions.length - 1);
        setQuestionIndexRaw(clampedIndex);
        return null;
    }

    // Helper: true if question has a real answer (not 0/undefined/"0")
    const isRealAnswer = (qId) => {
        const v = answers[qId];
        return v !== undefined && v !== 0 && v !== '0';
    };

    const handleOptionSelect = async (optionId) => {
        if (isPaused || isFinished || (mode === 'flash' && showFeedback)) return;

        const wasAlreadyAnswered = isRealAnswer(currentQuestion.id);

        setSelectedOption(optionId);
        submitAnswer(currentQuestion.id, optionId);

        if (mode === 'flash') {
            await loadAnswers(); // showFeedback becomes true automatically via useMemo
        } else if (mode === 'classic' && !isReviewMode && !wasAlreadyAnswered) {
            // Auto-advance: prefer n+1 if unanswered, otherwise first unanswered in the exam
            if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
            autoAdvanceTimerRef.current = setTimeout(() => {
                const nextIdx = currentQuestionIndex + 1;
                if (nextIdx < questions.length && !isRealAnswer(questions[nextIdx].id)) {
                    setCurrentQuestionIndex(nextIdx);
                } else {
                    const firstUnansweredIndex = questions.findIndex(
                        (q, idx) => idx !== currentQuestionIndex && !isRealAnswer(q.id)
                    );
                    if (firstUnansweredIndex !== -1) {
                        setCurrentQuestionIndex(firstUnansweredIndex);
                    }
                }
            }, 300);
        }
    };

    const handlePrev = () => {
        // In flash mode (non-review), allow going back to already-answered questions only
        if (mode === 'flash' && !isReviewMode && currentQuestionIndex > 0) {
            const prevQuestion = questions[currentQuestionIndex - 1];
            if (prevQuestion && isRealAnswer(prevQuestion.id)) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
            }
            return;
        }
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleNext = () => {
        // Flash mode: block advancing if current question not truly answered
        if (mode === 'flash' && !isReviewMode && !isRealAnswer(questions[currentQuestionIndex]?.id)) {
            return;
        }
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            // Last question reached
            if (mode === 'classic' && !isReviewMode) {
                // Find first unanswered question
                const firstUnansweredIndex = questions.findIndex(q => answers[q.id] === undefined);

                if (firstUnansweredIndex !== -1) {
                    // Go to first unanswered question
                    setCurrentQuestionIndex(firstUnansweredIndex);
                } else {
                    // All questions answered, trigger validation modal
                    setShowValidateModal(true);
                }
            }
        }
    };

    const handleRestart = (newMode) => {
        restartExam(newMode);
        setShowRestartModal(false);
        setShowSettings(false);
        setIsReviewMode(false);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const unansweredCount = questions.length - answeredCount;
    const allQuestionsAnswered = unansweredCount === 0;
    const progress = (answeredCount / questions.length) * 100;

    // Results screen
    if (isFinished && !isReviewMode) {
        return (
            <ExamResults
                passingScore={passingScore}
                setIsReviewMode={setIsReviewMode}
                showRestartModal={showRestartModal}
                setShowRestartModal={setShowRestartModal}
                onRestart={handleRestart}
                questions={questions}
                answers={answers}
                onDemandAnswers={onDemandAnswers}
                stopExam={stopExam}
                setCurrentQuestionIndex={setCurrentQuestionIndex}
                language={language}
                matiereResults={matiereResults}
                minSubScore={ctxMinSubScore}
                category={category}
            />
        );
    }

    return (
        <div className="fixed inset-0 bg-gray-50 dark:bg-black text-gray-900 dark:text-white flex flex-col z-[100] transition-colors duration-500">
            {/* Header — with inline MiniNav on desktop */}
            <ExamHeader
                isReviewMode={isReviewMode}
                showSettings={showSettings}
                setShowSettings={setShowSettings}
                setShowValidateModal={setShowValidateModal}
                showGrid={showGrid}
                setShowGrid={setShowGrid}
                setShowRestartModal={setShowRestartModal}
                stopExam={stopExam}
                isFinished={isFinished}
                answers={answers}
                hasAnnexe={hasAnnexe}
                examId={examId}
                annexeExamId={annexeExamId}
                title={examTitle}
                pauseSec={pauseSec}
                showAllAnswers={showAllAnswers}
                setPauseSec={setPauseSec}
                setShowAllAnswers={setShowAllAnswers}
                miniNav={
                    <ExamMiniNav
                        inline
                        isReviewMode={isReviewMode}
                        mode={mode}
                        questions={questions}
                        answers={answers}
                        currentQuestionIndex={currentQuestionIndex}
                        setCurrentQuestionIndex={setCurrentQuestionIndex}
                        onDemandAnswers={onDemandAnswers}
                        isHistoryVuReview={isHistoryVuReview}
                    />
                }
            />

            {/* Mini Navigator — mobile only (below header) */}
            <div data-help="session-mini-nav" className="md:hidden">
                <ExamMiniNav
                    isReviewMode={isReviewMode}
                    mode={mode}
                    questions={questions}
                    answers={answers}
                    currentQuestionIndex={currentQuestionIndex}
                    setCurrentQuestionIndex={setCurrentQuestionIndex}
                    onDemandAnswers={onDemandAnswers}
                    isHistoryVuReview={isHistoryVuReview}
                />
            </div>

            {/* Validate Confirmation Modal */}
            {showValidateModal && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-white/10 animate-in zoom-in-95 duration-200">
                        <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                            {allQuestionsAnswered
                                ? (language === 'fr' ? 'Valider l\'examen ?' : 'Validate Exam?')
                                : (language === 'fr' ? 'Questions non répondues' : 'Unanswered Questions')
                            }
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {allQuestionsAnswered
                                ? (language === 'fr'
                                    ? `Vous avez répondu à toutes les ${questions.length} questions. Voulez-vous valider l'examen ?`
                                    : `You have answered all ${questions.length} questions. Do you want to validate the exam?`)
                                : (language === 'fr'
                                    ? `Il reste ${unansweredCount} question(s) sans réponse. Vous devez répondre à toutes les questions avant de valider.`
                                    : `There are ${unansweredCount} unanswered question(s). You must answer all questions before validating.`)
                            }
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowValidateModal(false);
                                    if (!allQuestionsAnswered) {
                                        // Go to first unanswered question
                                        const firstUnansweredIdx = questions.findIndex(q =>
                                            answers[q.id] === undefined || answers[q.id] === 0 || answers[q.id] === '0'
                                        );
                                        if (firstUnansweredIdx !== -1) {
                                            setQuestionIndexRaw(firstUnansweredIdx);
                                        }
                                    }
                                }}
                                className="flex-1 px-6 py-3 bg-gray-200 dark:bg-white/10 rounded-xl font-bold hover:bg-gray-300 dark:hover:bg-white/20 transition text-gray-900 dark:text-white"
                            >
                                {allQuestionsAnswered
                                    ? (language === 'fr' ? 'Annuler' : 'Cancel')
                                    : (language === 'fr' ? 'Répondre aux questions' : 'Answer Questions')
                                }
                            </button>
                            {allQuestionsAnswered && (
                                <button
                                    onClick={() => {
                                        setShowValidateModal(false);
                                        finishExam();
                                    }}
                                    className="flex-1 px-6 py-3 bg-emerald-500 text-white rounded-xl font-bold hover:bg-emerald-600 transition shadow-lg shadow-emerald-500/20"
                                >
                                    {language === 'fr' ? 'Valider' : 'Validate'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ExamQuestion
                isReviewMode={isReviewMode}
                isPaused={isPaused}
                setIsPaused={setIsPaused}
                calculatedScore={calculatedScore}
                passingScore={passingScore}
                timeInSeconds={timeInSeconds}
                selectedOption={selectedOption}
                showFeedback={showFeedback}
                showAllAnswers={showAllAnswers}
                onOptionSelect={handleOptionSelect}
                onNext={handleNext}
                onPrev={handlePrev}
                formatTime={formatTime}
                currentQuestion={currentQuestion}
                currentQuestionIndex={currentQuestionIndex}
                questions={questions}
                answers={answers}
                onDemandAnswers={onDemandAnswers}
                language={language}
                mode={mode}
                finishExam={finishExam}
                setShowValidateModal={setShowValidateModal}
                matiereResults={matiereResults}
                minSubScore={ctxMinSubScore}
                category={category}
                sessionDate={historicalDate}
                isHistoryVuReview={isHistoryVuReview}
            />

            {/* Footer */}
            {!isReviewMode && (
                <footer data-help="session-footer" className="p-4 h-24 flex flex-col justify-end">
                    <div className="max-w-3xl mx-auto w-full flex items-center gap-6">
                        <button
                            onClick={() => setIsPaused(!isPaused)}
                            className="p-3 bg-white dark:bg-white/10 rounded-2xl hover:bg-gray-100 dark:hover:bg-white/20 transition border border-gray-200 dark:border-transparent text-gray-700 dark:text-white"
                        >
                            {isPaused ? (
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            )}
                        </button>

                        <div className="flex-1 space-y-1.5">
                            <div className="flex justify-between text-xs font-bold px-1">
                                {(() => {
                                    const maxSec = maxDurationMinutes ? maxDurationMinutes * 60 : null;
                                    const isOver = maxSec && timeInSeconds >= maxSec;
                                    const isWarning = maxSec && !isOver && timeInSeconds >= maxSec - 5 * 60;
                                    const colorClass = isOver
                                        ? 'text-red-500'
                                        : isWarning
                                            ? 'text-orange-500'
                                            : 'text-gray-500 dark:text-gray-400';
                                    return (
                                        <span className={colorClass}>
                                            {formatTime(timeInSeconds)}
                                            {maxDurationMinutes ? ` (Max ${maxDurationMinutes} min)` : ' min'}
                                        </span>
                                    );
                                })()}
                                <span className="text-gray-500 dark:text-gray-400">{Math.round(progress)}%</span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                                {(() => {
                                    const maxSec = maxDurationMinutes ? maxDurationMinutes * 60 : null;
                                    const isOver = maxSec && timeInSeconds >= maxSec;
                                    const isWarning = maxSec && !isOver && timeInSeconds >= maxSec - 5 * 60;
                                    const barColor = isOver
                                        ? 'bg-red-500'
                                        : isWarning
                                            ? 'bg-orange-500'
                                            : 'bg-blue-500';
                                    return (
                                        <div
                                            className={`h-full ${barColor} transition-all duration-300`}
                                            style={{ width: `${progress}%` }}
                                        />
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </footer >
            )}

            {/* Navigation Grid Overlay */}
            <ExamGridOverlay
                showGrid={showGrid}
                setShowGrid={setShowGrid}
                isReviewMode={isReviewMode}
                questions={questions}
                answers={answers}
                currentQuestionIndex={currentQuestionIndex}
                setCurrentQuestionIndex={setCurrentQuestionIndex}
                mode={mode}
                onDemandAnswers={onDemandAnswers}
                isHistoryVuReview={isHistoryVuReview}
            />

            {/* Global Restart Modal (triggered from anywhere) */}
            {
                showRestartModal && !isFinished && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
                        <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 max-w-md w-full border border-gray-200 dark:border-white/10 shadow-2xl animate-in zoom-in-95 duration-200">
                            <h3 className="text-2xl font-bold mb-4 text-center text-gray-900 dark:text-white">
                                {language === 'fr' ? 'Redémarrer l\'examen ?' : 'Restart Exam?'}
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 text-center mb-8">
                                {language === 'fr' ? 'Toute votre progression actuelle sera perdue.' : 'All your current progress will be lost.'}
                            </p>
                            <div className="space-y-4">
                                <button onClick={() => handleRestart('flash')} className="w-full p-4 bg-emerald-500 text-white rounded-2xl font-bold hover:bg-emerald-600 transition flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    {language === 'fr' ? 'Mode question-réponse' : 'Flashcard Mode'}
                                </button>
                                <button onClick={() => handleRestart('classic')} className="w-full p-4 bg-blue-500 text-white rounded-2xl font-bold hover:bg-blue-600 transition flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {language === 'fr' ? 'Mode examen' : 'Exam Mode'}
                                </button>
                                <button onClick={() => setShowRestartModal(false)} className="w-full p-3 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition mt-2">
                                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default ExamSession;
